export type TypeformUrlResult =
  | { ok: true; url: string; formId: string }
  | { ok: false; reason: string };

const TYPEFORM_TO_PATH_RE = /^\/to\/([A-Za-z0-9]+)\/?$/;

function isAllowedTypeformHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  if (host === "admin.typeform.com") return false;
  if (host === "form.typeform.com") return true;
  if (host.endsWith(".typeform.com")) return true;
  return false;
}

/**
 * Canonicalize a public Typeform form URL for embedding.
 *
 * Accepts typical public share URLs like:
 * - https://form.typeform.com/to/<FORM_ID>
 * - https://<workspace>.typeform.com/to/<FORM_ID>
 *
 * Returns a canonical URL on `form.typeform.com` preserving the querystring.
 */
export function normalizeTypeformUrl(input: string): TypeformUrlResult {
  const raw = (input ?? "").trim();
  if (!raw) return { ok: false, reason: "empty" };

  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return { ok: false, reason: "invalid_url" };
  }

  if (url.protocol !== "https:" && url.protocol !== "http:") {
    return { ok: false, reason: "invalid_scheme" };
  }

  if (!isAllowedTypeformHost(url.hostname)) {
    return { ok: false, reason: "not_typeform_domain" };
  }

  const match = TYPEFORM_TO_PATH_RE.exec(url.pathname);
  if (!match) {
    return { ok: false, reason: "invalid_path" };
  }

  const formId = match[1];
  const canonical = new URL(`https://form.typeform.com/to/${formId}`);
  canonical.search = url.search; // preserve URL params / hidden fields
  // Intentionally drop hash fragments for embeds.

  return { ok: true, url: canonical.toString(), formId };
}

export function normalizeTypeformUrlOrEmpty(input: string): TypeformUrlResult | { ok: true; url: ""; formId: "" } {
  const raw = (input ?? "").trim();
  if (!raw) return { ok: true, url: "", formId: "" };
  return normalizeTypeformUrl(raw);
}

